import { EditableProTable } from '@ant-design/pro-components'
import React, { useEffect, useMemo, useState } from 'react'
import { generateUuid } from 'utils/dataUtil'

import classes from './ValueTable.module.scss'
import FormSection from 'components/form-section'
import { Button, Form, Input, InputNumber } from 'antd'

const units = {
  PERMEABILITY: {
    FIELD: '(mD)',
    METRIC: '(mD)'
  },
  EFFECTIVE_THICKNESS: {
    FIELD: '(ft)',
    METRIC: '(m)'
  },
  FRACTURE_DENSITY: {
    FIELD: '(f/ft²)',
    METRIC: '(f/m²)'
  }
}

const ValueTable = ({ tab, fields = [], operation, simulationUnit, showFractureDensity}) => {
  const form = Form.useFormInstance()

  const [data, setData] = useState([])
  const updateFormationDepth = (value) => {
    form.setFieldValue(["RESERVOIR", "formation_depth"], value);
  }
  // Watch for reservoir type changes
  const reservoirType = Form.useWatch(['GENERAL', 'reservoir'], form)
  const fetchData = () => {
    const limitedFields = fields.slice(0,12)
    setData(limitedFields.map(field => {
      const rowData = form.getFieldValue(["RESERVOIR", tab, field.name])
      return {
        ...rowData,
        FRACTURE_DENSITY: rowData?.FRACTURE_DENSITY ?? null
      }
    }))
  }

  // Effect to update data when reservoir type changes
  useEffect(() => {
    if (reservoirType === 'Dualperm') {
      // Update form values to include FRACTURE_DENSITY
      fields.forEach((field) => {
        const currentValue = form.getFieldValue(["RESERVOIR", tab, field.name])
        if (!currentValue?.FRACTURE_DENSITY) {
          form.setFieldValue(["RESERVOIR", tab, field.name], {
            ...currentValue,
            FRACTURE_DENSITY: null
          })
        }
      })
      // Refresh the table data
      fetchData()
    }
  }, [reservoirType, fields, form, tab])

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const columns = useMemo(
    () => {
      const baseColumns = [
        {
          key: 'REGION',
          dataIndex: 'REGION',
          title: 'Region',
          editable: false
        },
        {
          key: 'POROSITY',
          dataIndex: 'POROSITY',
          title: 'Porosity',
          editable: true,
        },
        {
          key: 'PERMEABILITY',
          dataIndex: 'PERMEABILITY',
          title: `Permeability ${units.PERMEABILITY[simulationUnit] ?? ''}`,
          editable: true,
        },
        {
          key: 'EFFECTIVE_THICKNESS',
          dataIndex: 'EFFECTIVE_THICKNESS',
          title: `Effective thickness ${units.EFFECTIVE_THICKNESS[simulationUnit] ?? ''}`,
          editable: true,
        }
      ]

      if (showFractureDensity) {
        baseColumns.push({
          key: 'FRACTURE_DENSITY',
          dataIndex: 'FRACTURE_DENSITY',
          title: `Fracture Density ${units.FRACTURE_DENSITY[simulationUnit] ?? ''}`,
          editable: true,
        })
      }

      baseColumns.push({
        title: "Operation",
        valueType: "option",
        key: "operation",
        width: 160,
        render: (text, record, index, action) => [
          <Button
            key="editable"
            size="small"
            onClick={() => {
              action?.startEditable?.(record.REGION);
            }}
          >
            Edit
          </Button>
        ],
      })

      return baseColumns
    }, [simulationUnit, showFractureDensity])

  const limitedOperation = useMemo(() => ({
    ...operation,
    add: (...args) => {
      if (fields.length >= 12) {
        return; // Don't add if already at max
      }
      operation.add(...args);
    }
  }), [operation, fields.length]);

  return (
    <FormSection title={
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>Value</span>
        <span style={{ marginLeft: 'auto', paddingRight: 30, fontSize: '14px', color: '#888' }}>
          Formation Depth({simulationUnit === 'METRIC' ? 'm' : 'ft'})
        </span>
        <InputNumber
          value={form.getFieldValue(["RESERVOIR", "formation_depth"])}
          onChange={(value) => {
            updateFormationDepth(value);
          }}
        />
      </div>
    }>
      <EditableProTable
        options={false}
        pagination={false}
        search={false}
        columns={columns}
        value={data}
        rowKey="REGION"
        sticky={{
          offsetHeader: 32
        }}
        className={classes.tableContainer}
        cardProps={{
          className: classes.card
        }}
        tableClassName={classes.table}
        editable={{
          type: 'multiple',
          actionRender: (row, config, defaultDom) => [
            defaultDom.save,
            defaultDom.cancel,
          ],
          onSave: (rowKey, data, row) => {
            const { index, ...updatedRow } = data
            form.setFieldValue(["RESERVOIR", tab, index], updatedRow)
            fetchData()
          }
        }}
        scroll={{
          y: 380
        }}
        operation={limitedOperation}
        recordCreatorProps={{
          record: {
            key: generateUuid(),
            isNew: true
          },
          style: {
            display: fields.length >= 12 ? 'none' : undefined
          }
        }}
      />
    </FormSection>
  )
}

export default ValueTable